import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as LoadingIcons from "react-loading-icons";
import { FaPrint, FaEnvelope, FaPhone, FaMapMarkerAlt, FaUser } from "react-icons/fa";
import { useSelector } from "react-redux";
import { getFacture } from "../services/FactureService";
import { tvainfo } from "../services/LoginService";

const ConsulterFacture = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = useSelector((state) => state.auth.token);

    const [facture, setFacture] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tva_inclus, setTvaInclus] = useState(false);
    const [activeRates, setActiveRates] = useState([]);
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(true);

    // Récupération des données
    useEffect(() => {
        const fetchData = async () => {
            try {
                const factureData = await getFacture(id);
                setFacture(factureData);

                const resTVA = await tvainfo(token);
                if (resTVA.success) {
                    setTvaInclus(resTVA.tva_inclus ?? false);
                    const rates = resTVA.tva_rates || {};
                    const active = Object.keys(rates).filter(k => rates[k]).sort((a, b) => parseFloat(a) - parseFloat(b));
                    setActiveRates(active);
                }
            } catch (err) {
                toast.error(err.message);
                navigate("/EspaceClient/factures");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, navigate, token]);

    const openPdf = (id) => {
        setSelectedPdf(`http://localhost:8000/api/v1/factures/${id}/pdf?token=${token}`);
        setPdfLoading(true);
    };

    // Hooks useMemo doivent être **toujours appelés**
    const tvaParTaux = useMemo(() => {
        const result = {};
        activeRates.forEach(rate => result[parseFloat(rate).toFixed(2)] = 0);
        facture?.lignes?.forEach(ligne => {
            const totalHT = parseFloat(ligne.total_ligne_centimes) || 0;
            const tauxTVA = (parseFloat(ligne.taux_tva) || 0).toFixed(2);
            const montantTVA = (totalHT * parseFloat(tauxTVA)) / 100;
            if (!result[tauxTVA]) result[tauxTVA] = 0;
            result[tauxTVA] += montantTVA;
        });
        return result;
    }, [facture, activeRates]);

    const totaux = useMemo(() => {
        let sous_total = 0, remise = 0, tva = 0;
        facture?.lignes?.forEach(ligne => {
            const prix = parseFloat(ligne.prix_unitaire_centimes) || 0;
            const quantite = parseFloat(ligne.quantite) || 0;
            const tauxRemise = parseFloat(ligne.taux_remise) || 0;
            const tauxTVA = parseFloat(ligne.taux_tva) || 0;
            const ligneTotal = prix * quantite;
            const ligneRemise = ligneTotal * tauxRemise / 100;
            const ligneHT = ligneTotal - ligneRemise;
            const ligneTVA = tva_inclus ? ligneHT * tauxTVA / 100 : 0;
            sous_total += ligneHT;
            remise += ligneRemise;
            tva += ligneTVA;
        });
        return {
            sous_total_centimes: sous_total,
            remise_centimes: remise,
            tva_centimes: tva,
            total_centimes: sous_total + tva
        };
    }, [facture, tva_inclus]);

    if (loading) return (
        <div className="flex flex-col justify-center min-h-screen items-center">
            <LoadingIcons.SpinningCircles stroke="#2218eeff" fill="#2218eeff" strokeWidth={3} height={40} width={40} />
            <span className="ml-3 text-gray-600 font-medium">Chargement de la facture...</span>
        </div>
    );

    if (!facture) return null;

    const formatMoney = (value) => (value || 0).toFixed(2) + " €";

    return (
        <div className="min-h-screen p-6 flex flex-col gap-6">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* PDF Modal */}
            {selectedPdf && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-transparent w-11/12 h-full rounded-lg shadow-lg p-2 relative">
                        <button
                            onClick={() => setSelectedPdf(null)}
                            className="absolute top-2 right-2 text-white bg-red-600 px-2 py-1 rounded"
                        >
                            X
                        </button>

                        {pdfLoading && (
                            <div className="flex justify-center items-center h-full">
                                <LoadingIcons.SpinningCircles
                                    stroke="#fff"
                                    fill="#fff"
                                    strokeWidth={3}
                                    height={40}
                                    width={40}
                                />
                                <span className="ml-3 text-white font-medium">Chargement du PDF...</span>
                            </div>
                        )}

                        <iframe
                            src={selectedPdf}
                            title="Facture PDF"
                            className={`w-full h-full rounded-lg ${pdfLoading ? "hidden" : ""}`}
                            onLoad={() => setPdfLoading(false)}
                        />
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h1 className="font-bold text-2xl">Facture #{facture.numero}</h1>
                <button
                    onClick={() => openPdf(facture.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <FaPrint /> Pdf
                </button>
            </div>

            {/* Secteurs */}
            {(facture.secteurs_batiment?.length > 0 || facture.secteurs_autres?.length > 0) && (
                <div className="flex flex-wrap gap-2 mb-4">
                    {facture.secteurs_batiment?.map((secteur) => (
                        <span
                            key={`bat-${secteur.id}`}
                            className="px-3 py-1 rounded-full bg-green-500 text-white text-sm font-medium"
                        >
                            {secteur.nom}
                        </span>
                    ))}
                    {facture.secteurs_autres?.map((secteur) => (
                        <span
                            key={`autre-${secteur.id}`}
                            className="px-3 py-1 rounded-full bg-gray-400 text-white text-sm font-medium"
                        >
                            {secteur.nom}
                        </span>
                    ))}
                </div>
            )}

            {/* Émetteur & Client */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Émetteur */}
                <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                    <h2 className="font-semibold mb-2 flex items-center gap-2"><FaUser /> Émetteur</h2>
                    {facture.user?.nom_complet && <p><FaUser className="inline mr-1" /> {facture.user.nom_complet}</p>}
                    {facture.user?.email && <p><FaEnvelope className="inline mr-1" /> {facture.user.email}</p>}
                    {facture.user?.telephone && <p><FaPhone className="inline mr-1" /> {facture.user.telephone}</p>}
                    {facture.user?.adresse_ligne1 && <p><FaMapMarkerAlt className="inline mr-1" /> {facture.user.adresse_ligne1}</p>}
                    {facture.user?.adresse_ligne2 && <p><FaMapMarkerAlt className="inline mr-1" /> {facture.user.adresse_ligne2}</p>}
                    {facture.user?.ville && <p><FaMapMarkerAlt className="inline mr-1" /> {facture.user.ville} {facture.user.code_postal}</p>}
                </div>

                {/* Client */}
                <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                    <h2 className="font-semibold mb-2 flex items-center gap-2"><FaUser /> Destinataire</h2>
                    {facture.client?.nom_affichage && <p><FaUser className="inline mr-1" /> {facture.client.nom_affichage}</p>}
                    {facture.client?.nom_contact && <p><FaUser className="inline mr-1" /> Contact: {facture.client.nom_contact}</p>}
                    {facture.client?.email && <p><FaEnvelope className="inline mr-1" /> {facture.client.email}</p>}
                    {facture.client?.telephone && <p><FaPhone className="inline mr-1" /> {facture.client.telephone}</p>}
                    {facture.client?.adresse_ligne1 && <p><FaMapMarkerAlt className="inline mr-1" /> {facture.client.adresse_ligne1}</p>}
                    {facture.client?.adresse_ligne2 && <p><FaMapMarkerAlt className="inline mr-1" /> {facture.client.adresse_ligne2}</p>}
                    {facture.client?.ville && <p><FaMapMarkerAlt className="inline mr-1" /> {facture.client.code_postal} {facture.client.ville}</p>}
                    {facture.client?.numero_tva && <p>TVA : {facture.client.numero_tva}</p>}
                    {facture.client?.siret && <p>SIRET : {facture.client.siret}</p>}
                </div>
            </div>

            {/* Infos facture */}
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                <h2 className="font-semibold mb-2">Informations de la facture</h2>
                <p><strong>Date d'émission :</strong> {facture.date_emission}</p>
                <p><strong>Date d'échéance :</strong> {facture.date_echeance}</p>
                <p><strong>Statut :</strong> {facture.statut}</p>
                {facture.titre && <p><strong>Titre :</strong> {facture.titre}</p>}
                {facture.notes_client && <p><strong>Notes client :</strong> {facture.notes_client}</p>}
                {facture.notes_internes && <p><strong>Notes internes :</strong> {facture.notes_internes}</p>}
            </div>

            {/* Lignes */}
            <div className="bg-white shadow-md rounded-lg p-4">
                <h2 className="font-semibold mb-2">Lignes</h2>
                <table className="w-full text-sm border-collapse text-center">
                    <thead className="bg-color1 text-color2 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-3 py-2 text-left">Désignation</th>
                            <th className="px-3 py-2 w-24">Qté</th>
                            <th className="px-3 py-2 w-24">Unité</th>
                            <th className="px-3 py-2 w-32">Prix Unitaire</th>
                            {facture.tva_centimes > 0 && <th className="px-3 py-2 w-24">TVA (%)</th>}
                            {facture.remise_centimes > 0 && <th className="px-3 py-2 w-24">Remise (%)</th>}
                            <th className="px-3 py-2 w-32">Total HT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {facture.lignes.map((ligne, index) => {
                            const totalHT = (ligne.prix_unitaire_centimes || 0) * (ligne.quantite || 0);
                            return (
                                <tr key={index} className="border-b hover:bg-gray-50">
                                    <td className="px-2 py-1 text-left">{ligne.designation}</td>
                                    <td className="px-2 py-1">{ligne.quantite}</td>
                                    <td className="px-2 py-1">{ligne.unite || "-"}</td>
                                    <td className="px-2 py-1">{formatMoney(ligne.prix_unitaire_centimes, facture.devise)}</td>
                                    {facture.tva_centimes > 0 && <td className="px-2 py-1">{ligne.taux_tva || "-"}</td>}
                                    {facture.remise_centimes > 0 && <td className="px-2 py-1">{ligne.taux_remise || "-"}</td>}
                                    <td className="px-2 py-1">{formatMoney(totalHT, facture.devise)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>


                {/* Totaux */}
                <div className="grid grid-cols-[140px_120px] gap-y-1 justify-end items-end">
                    {/* Total HT */}
                    {totaux.sous_total_centimes > 0 && (
                        <>
                            <span className="font-bold text-left">Total HT :</span>
                            <span className="text-right">{totaux.sous_total_centimes.toFixed(2)} €</span>
                        </>
                    )}

                    {/* Remise */}
                    {totaux.remise_centimes > 0 && (
                        <>
                            <span className="font-bold text-left">Remise :</span>
                            <span className="text-right">{totaux.remise_centimes.toFixed(2)} €</span>
                        </>
                    )}

                    {/* TVA par taux */}
                    {tva_inclus &&
                        Object.keys(tvaParTaux).length > 0 &&
                        activeRates
                            .filter((rate) => (tvaParTaux[parseFloat(rate).toFixed(2)] || 0) > 0)
                            .map((rate) => {
                                const key = parseFloat(rate).toFixed(2);
                                return (
                                    <React.Fragment key={key}>
                                        <span className="font-bold text-left">TVA ({rate}) :</span>
                                        <span className="text-right">{tvaParTaux[key].toFixed(2)} €</span>
                                    </React.Fragment>
                                );
                            })}

                    {/* Total TVA */}
                    {totaux.tva_centimes > 0 && (
                        <>
                            <span className="font-bold text-left">Total TVA :</span>
                            <span className="text-right">{totaux.tva_centimes.toFixed(2)} €</span>

                            {/* Total TTC */}
                            <span className="font-bold text-left">Total TTC :</span>
                            <span className="text-right">{totaux.total_centimes.toFixed(2)} €</span>
                        </>
                    )}

                    {/* Acompte */}
                    {facture.acompte_centimes > 0 && (
                        <>
                            <span className="font-bold text-left text-green-600">Acompte :</span>
                            <span className="text-right text-green-600 font-semibold">{facture.acompte_centimes.toFixed(2)} €</span>

                            {/* Net à payer */}
                            {tva_inclus && totaux.total_centimes > 0 && (
                                <>
                                    <span className="font-bold text-left">Reste à payer :</span>
                                    <span className="text-right">{(totaux.total_centimes - facture.acompte_centimes).toFixed(2)} €</span>
                                </>
                            )}
                        </>
                    )}
                </div>



            </div>
        </div>
    );
};

export default ConsulterFacture;
